
import React from 'react';
import { Button } from '@/components/ui/button';
import { Grid3X3, LayoutList, MoreHorizontal } from 'lucide-react';
import EmptyMediaState from './EmptyMediaState';

interface MediaFile {
  id: string;
  name: string;
  type: 'audio' | 'image' | 'video' | 'document';
  url: string;
  thumbnail?: string;
  dateAdded: Date;
  size: number;
  format: string;
  duration?: number;
  tags: string[];
}

interface MediaLibraryOrganizerProps {
  mediaType: string;
  selectedFiles: string[];
  onSelectFiles: (files: string[]) => void;
  searchQuery: string;
}

// Mock data for demonstration
const mockMediaFiles: MediaFile[] = [
  {
    id: 'file-1',
    name: 'New Beat Idea',
    type: 'audio',
    url: '/audio/beat-idea.mp3',
    thumbnail: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d',
    dateAdded: new Date(2023, 6, 15),
    size: 4500000,
    format: 'mp3',
    duration: 180,
    tags: ['Beat', 'Work in Progress', 'Hip Hop']
  },
  {
    id: 'file-2',
    name: 'Album Cover Draft',
    type: 'image',
    url: '/images/album-cover.jpg',
    thumbnail: 'https://images.unsplash.com/photo-1459749411175-04bf5292ceea',
    dateAdded: new Date(2023, 7, 2),
    size: 2100000,
    format: 'jpg',
    tags: ['Artwork', 'Album Cover', 'Draft']
  },
  {
    id: 'file-3',
    name: 'Vocal Recording',
    type: 'audio',
    url: '/audio/vocals.wav',
    thumbnail: 'https://images.unsplash.com/photo-1520523839897-bd0b52f945a0',
    dateAdded: new Date(2023, 7, 10),
    size: 24000000,
    format: 'wav',
    duration: 210,
    tags: ['Vocals', 'Raw', 'Session']
  }
];

const MediaLibraryOrganizer: React.FC<MediaLibraryOrganizerProps> = ({ 
  mediaType, 
  selectedFiles, 
  onSelectFiles,
  searchQuery
}) => {
  const [viewMode, setViewMode] = React.useState<'grid' | 'list'>('grid');
  
  // Filter media files based on type and search query
  const filteredFiles = React.useMemo(() => {
    let files = mockMediaFiles;
    
    if (mediaType !== 'all') {
      files = files.filter(file => {
        if (mediaType === 'audio') return file.type === 'audio';
        if (mediaType === 'artwork') return file.type === 'image';
        if (mediaType === 'video') return file.type === 'video';
        return true;
      });
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      files = files.filter(file => 
        file.name.toLowerCase().includes(query) || 
        file.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    return files;
  }, [mediaType, searchQuery]);
  
  const toggleFileSelection = (fileId: string) => {
    if (selectedFiles.includes(fileId)) {
      onSelectFiles(selectedFiles.filter(id => id !== fileId));
    } else {
      onSelectFiles([...selectedFiles, fileId]);
    }
  };
  
  const selectAllFiles = () => {
    onSelectFiles(filteredFiles.map(file => file.id));
  };
  
  if (filteredFiles.length === 0) {
    return (
      <EmptyMediaState 
        activeTab={mediaType} 
        hasFilters={searchQuery.length > 0}
      />
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
            className="h-8 w-8 p-0"
          >
            <Grid3X3 className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
            className="h-8 w-8 p-0"
          >
            <LayoutList className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={selectAllFiles}>
            Select All
          </Button>
          <Button variant="outline" size="sm">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredFiles.map(file => (
            <div 
              key={file.id}
              className={`border rounded-lg overflow-hidden cursor-pointer transition-all ${
                selectedFiles.includes(file.id) ? 'ring-2 ring-primary' : 'hover:shadow-md'
              }`}
              onClick={() => toggleFileSelection(file.id)}
            >
              <div 
                className="aspect-square bg-muted-foreground/10"
                style={{
                  backgroundImage: file.thumbnail ? `url(${file.thumbnail})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              <div className="p-2">
                <h4 className="font-medium text-sm truncate">{file.name}</h4>
                <p className="text-xs text-muted-foreground">
                  {file.format.toUpperCase()} • {(file.size / 1000000).toFixed(1)} MB
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredFiles.map(file => (
            <div 
              key={file.id}
              className={`flex items-center border rounded-lg p-3 cursor-pointer transition-all ${
                selectedFiles.includes(file.id) ? 'ring-2 ring-primary' : 'hover:shadow-md'
              }`}
              onClick={() => toggleFileSelection(file.id)}
            >
              <div 
                className="h-12 w-12 rounded bg-muted-foreground/10 mr-3 flex-shrink-0"
                style={{
                  backgroundImage: file.thumbnail ? `url(${file.thumbnail})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              <div className="min-w-0 flex-1">
                <h4 className="font-medium truncate">{file.name}</h4>
                <div className="flex text-xs text-muted-foreground">
                  <span>{file.format.toUpperCase()}</span>
                  <span className="mx-2">•</span>
                  <span>{(file.size / 1000000).toFixed(1)} MB</span>
                  {file.duration && (
                    <>
                      <span className="mx-2">•</span>
                      <span>
                        {Math.floor(file.duration / 60)}:
                        {(file.duration % 60).toString().padStart(2, '0')}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="text-sm text-muted-foreground ml-4">
                {file.dateAdded.toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaLibraryOrganizer;
